import { ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import OrdersService from '@/api/services/orders.service';
import InvoicingAttemptService from '@/api/services/invoicing/invoicing-attempt.service';
import { useSocketStore } from './use-socket';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { CustomError } from '@/exceptions/custom.exception';
import { IInvoicingItem } from '@/interfaces/IInvoicingItem';
import {
  IInvoicingAttemptState,
  IInvoicingAttemptWsPayload,
  isInvoiceProcessingResponse,
  TInvoiceIssueResponse,
} from '@/interfaces/IInvoicingAttempt';

/**
 * Typed invoicing failure. Extends CustomError so `useUi().serverError`
 * surfaces `message` verbatim; `code` lets call sites branch on the error
 * taxonomy (NEEDS_REVIEW, PROVIDER_UNAVAILABLE, INVOICE_IN_PROGRESS, ...).
 */
export class InvoiceAttemptError extends CustomError {
  constructor(public readonly code: string, message: string) {
    super(message);
  }
}

/** Local (not server-issued) code: attempt still live after the poll cap. */
export const INVOICE_POLL_TIMEOUT = 'POLL_TIMEOUT';

const NEEDS_REVIEW_MESSAGE =
  'La factura requiere revisión. Contacte a soporte.';
const POLL_TIMEOUT_MESSAGE =
  'La factura sigue en proceso. Vuelva a consultar en unos minutos.';
const DEFAULT_POLL_MS = 3000;
const MAX_POLLS = 60;

/**
 * Module-level on purpose: attempts must outlive the component that started
 * them (the user can close the modal while the invoice settles in the
 * background), and every call site must agree on which orders are busy.
 */
const idempotencyKeys = new Map<string, string>();
const liveOrderIds = ref<number[]>([]);

function targetKey(orderIds: number[]): string {
  return [...orderIds].sort((a, b) => a - b).join(',');
}

function markLive(orderIds: number[]): void {
  liveOrderIds.value = [...new Set([...liveOrderIds.value, ...orderIds])];
}

function clearLive(orderIds: number[]): void {
  liveOrderIds.value = liveOrderIds.value.filter(
    (id) => !orderIds.includes(id)
  );
}

/** Extracts the server's typed `{ code, message }` error body, if present. */
function typedErrorFrom(err: unknown): InvoiceAttemptError | null {
  if (typeof err !== 'object' || err === null) return null;
  const response = (err as Record<string, unknown>).response;
  if (typeof response !== 'object' || response === null) return null;
  const data = (response as Record<string, unknown>).data;
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (typeof body.code === 'string' && typeof body.message === 'string') {
    return new InvoiceAttemptError(body.code, body.message);
  }
  return null;
}

/**
 * Settles a parked (202) attempt: polls the attempt endpoint every
 * `pollAfterMs` (WS `invoicingAttempt` events short-circuit the wait) until
 * a terminal state. Self-contained cleanup — no component lifecycle needed.
 */
function settleProcessing(
  publicId: string,
  pollAfterMs: number
): Promise<IInvoicingItem> {
  const socketStore = useSocketStore();
  const restaurantStore = useRestaurantStore();
  const socket = socketStore.socket;
  const restaurantId = restaurantStore.restaurant?.id;
  const wsEvent = restaurantId ? `r:${restaurantId}:invoicingAttempt` : null;

  return new Promise<IInvoicingItem>((resolve, reject) => {
    let settled = false;
    let polls = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (wsEvent && socket) socket.off(wsEvent, onWsUpdate);
    };

    const finishResolve = (item: IInvoicingItem) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(item);
    };

    const finishReject = (err: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    };

    const applyState = (state: IInvoicingAttemptState) => {
      if (state.status === 'succeeded') {
        if (state.invoicingItem) finishResolve(state.invoicingItem);
        else
          finishReject(
            new InvoiceAttemptError('NEEDS_REVIEW', NEEDS_REVIEW_MESSAGE)
          );
        return;
      }
      if (state.status === 'failed') {
        finishReject(
          new InvoiceAttemptError(
            state.errorCode ?? 'UNKNOWN',
            state.errorMessage ?? 'Error al crear la factura'
          )
        );
        return;
      }
      if (state.status === 'needs_review') {
        finishReject(
          new InvoiceAttemptError('NEEDS_REVIEW', NEEDS_REVIEW_MESSAGE)
        );
      }
      // pending / issuing / verifying: keep polling.
    };

    const poll = async () => {
      if (settled) return;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      polls++;
      try {
        const state = await InvoicingAttemptService.getAttempt(publicId);
        applyState(state);
      } catch (err) {
        // Transient (network blip): the next scheduled poll retries.
        console.error('[useInvoiceAttempt] Poll failed', err);
      }
      scheduleNext();
    };

    const scheduleNext = () => {
      if (settled || timer) return;
      if (polls >= MAX_POLLS) {
        finishReject(
          new InvoiceAttemptError(INVOICE_POLL_TIMEOUT, POLL_TIMEOUT_MESSAGE)
        );
        return;
      }
      timer = setTimeout(() => {
        poll().catch(() => undefined);
      }, pollAfterMs);
    };

    const onWsUpdate = (payload: IInvoicingAttemptWsPayload) => {
      if (settled || payload?.publicId !== publicId) return;
      // WS payloads carry no invoicing item: fetch the full state now.
      poll().catch(() => undefined);
    };

    if (wsEvent && socket) socket.on(wsEvent, onWsUpdate);
    scheduleNext();
  });
}

async function runIssueRequest(
  orderIds: number[],
  post: (idempotencyKey: string) => Promise<TInvoiceIssueResponse>
): Promise<IInvoicingItem> {
  const target = targetKey(orderIds);
  // Reuse the pending key so a retry after an unknown outcome (network cut,
  // dropped response) replays the original attempt instead of emitting twice.
  const idempotencyKey = idempotencyKeys.get(target) ?? uuidv4();
  idempotencyKeys.set(target, idempotencyKey);

  markLive(orderIds);
  try {
    let response: TInvoiceIssueResponse;
    try {
      response = await post(idempotencyKey);
    } catch (err) {
      const typed = typedErrorFrom(err);
      if (typed) {
        // Journaled terminal failure: the key now permanently maps to that
        // failed attempt server-side, so the next retry needs a fresh key.
        idempotencyKeys.delete(target);
        throw typed;
      }
      // No server verdict: keep the key for an idempotent replay.
      throw err;
    }

    if (isInvoiceProcessingResponse(response)) {
      try {
        const item = await settleProcessing(
          response.attemptPublicId,
          response.pollAfterMs || DEFAULT_POLL_MS
        );
        idempotencyKeys.delete(target);
        return item;
      } catch (err) {
        // On poll timeout the attempt may still be live: keep the key so a
        // retry reattaches to it. Any terminal error clears it.
        if (
          !(err instanceof InvoiceAttemptError) ||
          err.code !== INVOICE_POLL_TIMEOUT
        ) {
          idempotencyKeys.delete(target);
        }
        throw err;
      }
    }

    idempotencyKeys.delete(target);
    return response;
  } finally {
    clearLive(orderIds);
  }
}

/**
 * Invoice issuing with background settlement. Lifecycle-free by design (a
 * factory of plain async functions): usable from class components, and the
 * returned promises keep settling after the calling component unmounts.
 */
export function useInvoiceAttempt() {
  async function createInvoice(
    orderId: number,
    invoiceData: { invoiceType: number; idType: number; id: string }
  ): Promise<IInvoicingItem> {
    return await runIssueRequest([orderId], (idempotencyKey) =>
      OrdersService.createInvoice(orderId, {
        ...invoiceData,
        idempotencyKey,
        acceptPending: true,
      })
    );
  }

  async function massCreateInvoice(
    orderIds: number[],
    invoiceData: { invoiceType: number; idType?: number; id?: string }
  ): Promise<IInvoicingItem> {
    return await runIssueRequest(orderIds, (idempotencyKey) =>
      OrdersService.massCreateInvoice(orderIds, invoiceData, {
        idempotencyKey,
        acceptPending: true,
      })
    );
  }

  /** Reactive: true while an attempt started here covers the order. */
  function isInvoiceLive(orderId: number): boolean {
    return liveOrderIds.value.includes(orderId);
  }

  return {
    createInvoice,
    massCreateInvoice,
    isInvoiceLive,
  };
}
